import React from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  NumberField,
  BooleanField,
  FunctionField,
  useRecordContext,
  EditButton,
  TopToolbar,
  ListButton,
} from 'react-admin';
import { Card, CardContent, Typography, Grid, Box, Chip } from '@mui/material';
import { PageWrapper } from '../../components/PageWrapper';

const ShowActions = () => (
  <TopToolbar>
    <ListButton />
    <EditButton />
  </TopToolbar>
);

const ExpiryStatusChip = () => {
  const record = useRecordContext();

  if (!record || !record.expiryDate) return null;

  const expiryDate = new Date(record.expiryDate);
  const today = new Date();
  const daysUntilExpiry = Math.floor((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  let color: 'error' | 'warning' | 'success' = 'success';
  let label = 'Active';

  if (daysUntilExpiry < 0) {
    color = 'error';
    label = 'Expired';
  } else if (daysUntilExpiry <= 30) {
    color = 'warning';
    label = `Expires in ${daysUntilExpiry} days`;
  }

  return <Chip label={label} color={color} />;
};

export const InsuranceShow = () => {
  return (
    <PageWrapper title="Insurance Policy Details">
      <Show actions={<ShowActions />}>
        <SimpleShowLayout>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="bold" color="primary">
                  Policy Information
                </Typography>
                <ExpiryStatusChip />
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Policy Number
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>
                      <TextField source="policyNumber" />
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Insurer Name
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>
                      <TextField source="insurerName" />
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Policy Name
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      <TextField source="policyName" />
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
                Coverage Period
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Effective Date
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      <DateField source="effectiveDate" />
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Expiry Date
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      <DateField source="expiryDate" />
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
                Coverage Details
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Liability Limit
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>
                      <NumberField
                        source="liabilityLimit"
                        options={{
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }}
                      />
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Collision Deductible
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>
                      <NumberField
                        source="collisionDeductible"
                        options={{
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }}
                      />
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Comprehensive Deductible
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>
                      <NumberField
                        source="comprehensiveDeductible"
                        options={{
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }}
                      />
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      All Perils Deductible
                    </Typography>
                    <Typography variant="h6" sx={{ mt: 0.5 }}>
                      <NumberField
                        source="allPerilsDeductible"
                        options={{
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }}
                      />
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h5" fontWeight="bold" color="primary" gutterBottom>
                Additional Information
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Notes
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      <TextField source="notes" />
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="medium">
                      Status
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      <BooleanField source="active" />
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </SimpleShowLayout>
      </Show>
    </PageWrapper>
  );
};
